/**
 * Subunit Store
 * Zustand state management for subunit editor interactions
 * Simplified version of obstacleStore (no presets, no shapes, no scheduling)
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { SubunitCategory } from '@/types/subunit'
import { SUBUNIT_CATEGORY_COLORS } from '@/types/subunit'

/** Default values for creating a new subunit */
export interface CreateSubunitDefaults {
  name: string
  category: SubunitCategory
  width: number
  depth: number
  height: number
  elevationFt: number
  clearanceBufferFt: number
  deductFromParent: boolean
  color: string
}

interface SubunitState {
  /** Currently selected subunit ID */
  selectedSubunitId: string | null
  /** Currently hovered subunit ID */
  hoveredSubunitId: string | null
  /** Whether user is in placing mode (adding new subunit from panel) */
  isPlacingMode: boolean
  /** Whether subunit is being dragged */
  isDragging: boolean
  /** Cursor world position during placement (scene feet) */
  previewPosition: { x: number; z: number } | null
  /** Subunit ID to focus camera on */
  focusSubunitId: string | null
  /** Controls 3D mesh + label visibility */
  subunitsVisible: boolean
  /** When true, subunit position is clamped to parent unit bounds */
  constrainToParent: boolean
  /** Form values for the ghost + creation (panel placing mode) */
  placingDefaults: CreateSubunitDefaults | null
  /** True when the floating create form is open (view-mode placement) */
  isCreatingMode: boolean
  /** Target building for "Add Subunit" (null = auto-detect on click) */
  creatingForBuildingId: string | null
  /** Form values for the ghost + creation mutation (view-mode) */
  creatingDefaults: CreateSubunitDefaults | null
}

interface SubunitActions {
  selectSubunit: (id: string | null) => void
  setHovered: (id: string | null) => void
  startPlacing: (defaults: CreateSubunitDefaults) => void
  cancelPlacing: () => void
  setDragging: (dragging: boolean) => void
  setPreviewPosition: (pos: { x: number; z: number } | null) => void
  requestFocusOnSubunit: (id: string | null) => void
  clearSelection: () => void
  toggleSubunitsVisible: () => void
  toggleConstrainToParent: () => void
  startCreating: (buildingId: string | null) => void
  cancelCreating: () => void
  setCreatingDefaults: (defaults: CreateSubunitDefaults) => void
  reset: () => void
}

const DEFAULT_CREATING_DEFAULTS: CreateSubunitDefaults = {
  name: 'New Office',
  category: 'office',
  width: 20,
  depth: 15,
  height: 10,
  elevationFt: 0,
  clearanceBufferFt: 1.5,
  deductFromParent: true,
  color: SUBUNIT_CATEGORY_COLORS.office,
}

const initialState: SubunitState = {
  selectedSubunitId: null,
  hoveredSubunitId: null,
  isPlacingMode: false,
  isDragging: false,
  previewPosition: null,
  focusSubunitId: null,
  subunitsVisible: true,
  constrainToParent: true,
  placingDefaults: null,
  isCreatingMode: false,
  creatingForBuildingId: null,
  creatingDefaults: null,
}

export const useSubunitStore = create<SubunitState & SubunitActions>()(
  devtools(
    (set) => ({
      ...initialState,

      selectSubunit: (id) =>
        set({
          selectedSubunitId: id,
          isPlacingMode: false,
          placingDefaults: null,
          previewPosition: null,
        }),

      setHovered: (id) => set({ hoveredSubunitId: id }),

      startPlacing: (defaults) =>
        set({
          isPlacingMode: true,
          placingDefaults: defaults,
          selectedSubunitId: null,
        }),

      cancelPlacing: () =>
        set({ isPlacingMode: false, placingDefaults: null, previewPosition: null }),

      setDragging: (dragging) => set({ isDragging: dragging }),

      setPreviewPosition: (pos) => set({ previewPosition: pos }),

      requestFocusOnSubunit: (id) => set({ focusSubunitId: id }),

      clearSelection: () =>
        set({ selectedSubunitId: null, hoveredSubunitId: null }),

      toggleSubunitsVisible: () =>
        set((state) => ({
          subunitsVisible: !state.subunitsVisible,
          ...(state.subunitsVisible
            ? { selectedSubunitId: null, hoveredSubunitId: null, isPlacingMode: false, placingDefaults: null, isCreatingMode: false, creatingDefaults: null, creatingForBuildingId: null, previewPosition: null }
            : {}),
        })),

      toggleConstrainToParent: () =>
        set((state) => ({ constrainToParent: !state.constrainToParent })),

      startCreating: (buildingId) => {
        set({
          isCreatingMode: true,
          creatingForBuildingId: buildingId,
          creatingDefaults: { ...DEFAULT_CREATING_DEFAULTS },
          selectedSubunitId: null,
          isPlacingMode: false,
          placingDefaults: null,
        })
      },

      cancelCreating: () =>
        set({
          isCreatingMode: false,
          creatingForBuildingId: null,
          creatingDefaults: null,
          previewPosition: null,
        }),

      setCreatingDefaults: (defaults) =>
        set({ creatingDefaults: defaults }),

      // Preserve subunitsVisible and constrainToParent across resets
      reset: () => set((state) => ({ ...initialState, subunitsVisible: state.subunitsVisible, constrainToParent: state.constrainToParent })),
    }),
    { name: 'subunit-store' }
  )
)
